import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { HEADLINES } from "../lib/constants";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/d7525a52-8808-4719-9077-616ff5857b1e/images/b530fb225431ce985d9145b2c287455111316d545e0701065deebc762cb681d7.png";

export const Hero = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HEADLINES.length), 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex flex-col justify-end overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Deep ocean luxury"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#02060D] via-[#02060D]/85 to-[#02060D]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#02060D] via-transparent to-[#02060D]/30" />
      </div>

      {/* Side rail removed for cleaner hero */}

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pt-32 pb-20 md:pb-28">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="text-xs tracking-[0.3em] uppercase text-[#D1B677] font-medium">
            A Premium Global Business Gateway
          </span>
        </motion.div>

        {/* Rotating headline */}
        <div className="min-h-[260px] sm:min-h-[300px] lg:min-h-[380px] mb-8 max-w-5xl">
          <AnimatePresence mode="wait">
            <motion.h1
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-headline"
              className="font-serif text-white text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[1.05]"
            >
              {HEADLINES[idx].split(" ").map((word, i) => {
                const accent = ["Global", "Goa's", "Goa", "World", "Investors"].some(
                  (k) => word.replace(/[^\w]/g, "").toLowerCase().includes(k.toLowerCase()),
                );
                return (
                  <span
                    key={i}
                    className={accent ? "italic text-[#D1B677] font-normal" : ""}
                  >
                    {word}{" "}
                  </span>
                );
              })}
            </motion.h1>
          </AnimatePresence>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-base lg:text-lg text-[#A1AEBF] max-w-2xl leading-relaxed mb-10"
        >
          A trusted platform enabling businesses, investors, enterprises and strategic
          partners to discover, connect and collaborate on high-value opportunities
          across Goa.
        </motion.p>

        {/* FREE badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          data-testid="hero-free-badge"
          className="inline-flex items-center gap-3 border border-[#D1B677]/40 bg-[#D1B677]/[0.06] backdrop-blur-md px-5 py-3 mb-10"
        >
          <span className="w-1.5 h-1.5 bg-[#D1B677] rounded-full animate-pulse" />
          <span className="text-xs tracking-[0.2em] uppercase text-[#D1B677] font-medium">
            FREE Registration · Goa Businesses & Service Providers Only
          </span>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#opportunities"
            data-testid="hero-explore-btn"
            className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#D1B677] text-[#02060D] text-sm font-semibold hover:bg-[#E6CC8A] transition-all"
          >
            Explore Business Opportunities
            <ArrowRight
              size={18}
              weight="bold"
              className="group-hover:translate-x-1 transition-transform"
            />
          </a>
          <a
            href="#register"
            data-testid="hero-register-btn"
            className="group inline-flex items-center justify-center gap-3 px-7 py-4 border border-white/20 text-white text-sm hover:border-[#D1B677] hover:text-[#D1B677] transition-all"
          >
            Register Your Business Free
            <ArrowUpRight
              size={18}
              weight="light"
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
            />
          </a>
        </motion.div>
      </div>

      {/* Bottom marquee */}
      <div className="relative z-10 border-t border-white/5 bg-[#02060D]/60 backdrop-blur-md overflow-hidden">
        <div className="flex gs-marquee-track whitespace-nowrap py-5">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-12 px-6 shrink-0">
              {[
                "Tourism & Hospitality",
                "International Trade",
                "Luxury Real Estate",
                "Strategic Partnerships",
                "Startup Investment",
                "Coastal & Maritime",
                "Wellness & Retreats",
                "Manufacturing & MSME",
              ].map((s, i) => (
                <span
                  key={`${k}-${i}`}
                  className="text-xs tracking-[0.3em] uppercase text-[#A1AEBF]/60 flex items-center gap-12"
                >
                  {s}
                  <span className="text-[#D1B677]/40">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
